import { formatDate, formatNumber } from '@angular/common';
import { ListeCodeStatutsLivraison, ListeStatutsFacture } from 'src/app/board/activity/activity.constants';
import { KalDate } from '@kalidea/kaligraphi';
import { downloadFile } from 'src/app/utils/file.utils';
import { formatIban } from 'src/app/utils/iban.utils';

export interface ColumnsCSV<T> {
    label: string;
    code: keyof T;
    exporter?: (row: T, code: keyof T) => string;
}

export function exportAmount<T>(row: T, code: keyof T): string {
    return row[code as string] ? formatNumber(row[code as string], 'fr-FR', '1.2').replace(/\s/g, ' ').replace(/\./g,',') : '';
}

export function exportAmountOrZero<T>(row: T, code: keyof T): string {
    return exportAmount(row, code) || '0,00';
}

export function exportDeliveryStatusCodeToLabel<T>(row: T, columnCode: keyof T): string {
    return ListeCodeStatutsLivraison.find(({code}) => code === row[columnCode as string])?.label ?? '';
}

export function exportInvoiceStatusCodeToLabel<T>(row: T, code: keyof T): string {
    return ListeStatutsFacture.find(({code: statusCode}) => statusCode === row[code as string])?.label ?? '';
}

export function exportDate<T>(row: T, code: keyof T): string {
    return row[code as string] ? formatDate(row[code as string], 'dd/MM/yyyy', 'fr-FR') : '';
}

export function exportDateTime<T>(row: T, code: keyof T): string {
    return row[code as string] ? formatDate(row[code as string], 'dd/MM/yyyy à HH:mm:ss', 'fr-FR') : '';
}

export function exportBoolean<T>(row: T, code: keyof T): string {
    return !!row[code as string] ? 'Oui' : 'Non';
}

export function exportListAsCSV<T>(list: T[], columns: ColumnsCSV<T>[], fileName = 'export'): void {

    const extensionPos = fileName.search(/(\.csv)$/);
    if (extensionPos !== -1) {
        fileName = fileName.substring(0, extensionPos);
    }

    // on est obligé de rajouter un tiret entre chaque paramètre du format, sinon le parser ne fonctionne pas
    fileName = `${fileName}_${new KalDate().toFormat('yyyy-MM-dd')}.csv`;

    const separator = ';';
    const linesList: string[] = [];

    linesList.push(columns.map(c => `"${c.label}"`).join(separator));
    list.forEach(line => {
        // on utilise l'ordre des colonnes
        linesList.push(columns
            // si il n'a pas d'exporter, on renvoie simplement la donnée row[code]
            .map(({code, exporter}) => (exporter || ((r, c) => '' + (r[c] ?? '')))(line, code) || '')
            // formatage de l'affichage ( double quote + empty string instead of undefined/null ) + échappement des doubles quotes pour le csv
            .map((v: string) => `"${v?.replace(/"/g, '""') || ''}"`)
            .join(separator));
    });

    // build csv content from lines
    const csvContent = '\ufeff' + linesList.join('\r\n');

    downloadFile(csvContent, fileName, 'text/csv;charset=utf-8;encoding:utf-8');
}

export function exportIban<T>(row: T, code: keyof T): string {
    return row[code as string] ? formatIban((row['CodePays']  + row['CleIban'] + row['BBAN'])) : '';
}
